import {
    Box,
    Typography,
    makeStyles
} from "@material-ui/core";
import CustomModal from "./CustomModal";
import { useEffect, useMemo, useState } from "react";
import useAbortableFetch from "../../hooks/useAbortableFetch";
import AddRevenueSplitForm from "./AddRevenueSplitForm";
import EditListRevenueSplits from "./EditListRevenueSplits";
import { useSnackbarAlert } from '../../hooks/useSnackbarAlert';

const EDIT_WARNING_MESSAGE = 'Total revenue split percentage exceeds 100%';
const ZERO_REVENUE_SPLIT_WARNING_MESSAGE = 'If the total revenue split % is equal to 0% total revenue will be equal to $0.';
const ERROR_ALERT_MESSAGE = 'Error trying to save revenue split';
const SAVED_CHANGES_SUCCESS_MESSAGE = 'Changes saved successfully';

const useStyles = makeStyles((theme) => ({
    addNewButton: {
        color: theme.palette.primary.main,
        fontSize: '16px',
        fontWeight: 500,
        marginTop: theme.spacing(2),
        '&:hover': {
            cursor: 'pointer'
        }
    }
}));

const generateFileName = (selectedItems) => {
    if (selectedItems.length === 0) return '';

    return `${selectedItems[0].fileName} ${selectedItems.length > 1 ? ` + ${selectedItems.length - 1}` : ''}`
}

const ManageRevenueSplitModal = ({
    open,
    song,
    onClose,
    selectedItems,
    fetchRevenueSplits
}) => {
    const [warningMessage, setWarningMessage] = useState('');
    const [revenueSplits, setRevenueSplits] = useState([]);
    const [newAccountHolderInput, setNewAccountHolderInput] = useState({});
    const [mainAccountHolderId, setMainAccountHolderId] = useState(null);
    const [inputPercentageData, setInputPercentageData] = useState('');
    const [showAddRevenueSplit, setShowAddRevenueSplit] = useState(false);
    const [disabledApplyEdit, setDisabledApplyEdit] = useState(false);
    const { showErrorAlert, showSuccessAlert } = useSnackbarAlert();

    const managedFileNames = useMemo(() => generateFileName(selectedItems), []);

    const styles = useStyles();
    const abortableFetch = useAbortableFetch();

    useEffect(() => {
        handleGetRevenueSplits();
    }, []);

    useEffect(() => {
        const totalSplitPercentage = revenueSplits.reduce((prev, curr) => Number(curr.splitPercentage) + prev, Number(inputPercentageData) ?? 0);
        if (revenueSplits.length > 0) {
            if (totalSplitPercentage === 0) {
                setWarningMessage(ZERO_REVENUE_SPLIT_WARNING_MESSAGE);
                return;
            } else if (totalSplitPercentage > 100) {
                setWarningMessage(EDIT_WARNING_MESSAGE);
                return;
            }
        }
        setWarningMessage('');
    }, [revenueSplits, inputPercentageData]);

    const handleShowAddRevenueSplit = () => setShowAddRevenueSplit(true);

    const handleHideAddRevenueSplit = () => {
        setShowAddRevenueSplit(false);
        setNewAccountHolderInput({});
        setInputPercentageData('');
    }

    const handleSaveRevenueSplit = async () => {
        try {
            const splits = [...revenueSplits];
            if (newAccountHolderInput?.id) {
                splits.push({
                    accountHolderId: newAccountHolderInput.id,
                    splitPercentage: inputPercentageData,
                    name: newAccountHolderInput.name
                });
            }

            const revenueSplitPercentage = revenueSplits.reduce((prev, curr) => Number(curr.splitPercentage) + prev, Number(inputPercentageData));
            if (revenueSplitPercentage > 100 || revenueSplitPercentage < 0) {
                showErrorAlert(EDIT_WARNING_MESSAGE);
                return;
            }

            setRevenueSplits(splits);

            setWarningMessage('');
            handleHideAddRevenueSplit();
        } catch (error) {
            console.log(error);
            showErrorAlert(ERROR_ALERT_MESSAGE);
        }
    }

    const handleGetRevenueSplits = async () => {
        try {
            const reportAndSongIdPairs = selectedItems.flatMap(({ id, songIds }) => (songIds.map(songId => ({ reportId: id, songId: songId }))));

            const { revenueSplits, mainAccountHolderId } = await abortableFetch('POST', '/api/revenue-split/get-revenue-split', {
                body: {
                    reportAndSongIdPairs,
                    songId: song.id
                }
            });

            setRevenueSplits(revenueSplits);
            setMainAccountHolderId(mainAccountHolderId);
        } catch (error) {
            console.log(error);
            showErrorAlert(ERROR_ALERT_MESSAGE);
        }
    }

    const handleSaveChanges = async () => {
        try {
            const revenueSplitPercentage = revenueSplits.reduce((prev, curr) => Number(curr.splitPercentage) + prev, 0);
            if (revenueSplitPercentage > 100 || revenueSplitPercentage < 0 || disabledApplyEdit) {
                showErrorAlert(EDIT_WARNING_MESSAGE);
                return;
            }

            const reportAndSongIdPairs = selectedItems.flatMap(({ id, songIds }) => (songIds.map(songId => ({ reportId: id, songId: songId }))));

            const response = await abortableFetch('PUT', '/api/revenue-split', {
                body: {
                    reportAndSongIdPairs,
                    revenueSplits
                }
            });

            if (response?.success) {
                showSuccessAlert(SAVED_CHANGES_SUCCESS_MESSAGE);
            } else {
                showErrorAlert(ERROR_ALERT_MESSAGE);
            }

            await fetchRevenueSplits();
            await handleGetRevenueSplits();
        } catch (error) {
            console.log(error);
            showErrorAlert(ERROR_ALERT_MESSAGE);
        }
    }

    return (
        <CustomModal 
            open={open} 
            onClose={onClose}
            headerText={`Manage Revenue Split for song: ${song.artistName} - ${song.title}`}
            subHeaderText={managedFileNames}
            warningMessage={warningMessage}
            onSaveChanges={handleSaveChanges}
            disabledSaveButton={!!warningMessage || showAddRevenueSplit}
        >
            {revenueSplits.length > 0 && (
                <EditListRevenueSplits
                    revenueSplits={revenueSplits}
                    mainAccountHolderId={mainAccountHolderId}
                    setRevenueSplits={setRevenueSplits}
                    setDisabledApplyEdit={setDisabledApplyEdit}
                    showBulkEdit={false}
                />
            )}
            {showAddRevenueSplit ? (
                <AddRevenueSplitForm
                    mainAccountHolderId={mainAccountHolderId}
                    selectedAccountHolder={newAccountHolderInput}
                    setSelectedAccountHolder={setNewAccountHolderInput}
                    inputPercentageData={inputPercentageData}
                    setInputPercentageData={setInputPercentageData}
                    disabledSubmitButton={!!warningMessage}
                    onSubmit={handleSaveRevenueSplit}
                    onCancel={handleHideAddRevenueSplit}
                />
            ) : (
                <Typography
                    className={styles.addNewButton}
                    onClick={handleShowAddRevenueSplit}
                >
                    + Add new
                </Typography>
            )}
        </CustomModal>
    )
}

export default ManageRevenueSplitModal;